
import {createRouter, createWebHistory} from "vue-router";
import auth from "./middleware/auth";
import isStaff from "./middleware/isStaff";
import guest from "./middleware/guest";
import authRoutes from "./projects/auth";
import homework from "./projects/homework";
import { loadLanguageAsync } from "../i18n";
import courseRoutes from "./projects/course";
import journalRoutes from "./projects/journal";
import micRoutes from "./projects/capture";
import parentOnly from "./middleware/parentapp/parentOnly";
import resourceCenterRoutes from "./projects/resourcecenter";
import dashboardRoutes from "./projects/dashboard";
import hasSelectedChild from "./middleware/parentapp/hasSelectedChild";
import isCompass from "./middleware/isCompass";
import userExists from "./middleware/userExists";
import entryExists from "./middleware/journal/entryExists";
import commentExists from "./middleware/journal/commentExists";
import journalClassExists from "./middleware/journal/journalClassExists";
import teacherAreaRoutes from "./projects/teacherarea";
import { useAuthUserStore } from "@/stores/authUser";
import { useFormErrorsStore } from "@/stores/formErrors";

let appRoutes = [];

if (window.location.host.match(/^parent(test|-([^.]+))?\./)) {
    appRoutes = [
        {
            path: "",
            component: () => {
                return import(
                     "@/projects/parent_app/App.vue"
                );
            },
            children: [
                {
                    path: "login",
                    name: "login",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/parent_app/views/Login.vue"
                            );
                        }
                    },
                    meta: {
                        middleware: [guest],
                        title: "pp.login.title"
                    }
                },
                {
                    path: "register",
                    name: "register",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/parent_app/views/Register.vue"
                            );
                        }
                    },
                    meta: {
                        middleware: [guest],
                        title: "pp.register.title"
                    }
                },
                {
                    path: "forgot-password",
                    name: "forgot-password",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/parent_app/views/ForgotPassword.vue"
                            );
                        }
                    },
                    meta: {
                        middleware: [guest],
                        title: "pp.forgotpassword.title"
                    }
                },
                {
                    path: "reset-password/:token",
                    name: "reset-password",
                    props: true,
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/parent_app/views/ResetPassword.vue"
                            );
                        }
                    },
                    props: true,
                    meta: {
                        middleware: [guest],
                        title: "pp.resetpassword.title"
                    }
                },
                {
                    path: "link/:code?",
                    name: "link-child",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/parent_app/views/LinkChild.vue"
                            );
                        }
                    },
                    meta: {
                        middleware: [auth(false), parentOnly],
                        title: "pp.linkchild.title"
                    }
                },
                {
                    path: "",
                    name: "launch",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/parent_app/views/Launch.vue"
                            );
                        }
                    },
                    meta: {
                        middleware: [auth(false), parentOnly, hasSelectedChild],
                        title: "pp.launch.title"
                    }
                },
                {
                    path: "settings",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/parent_app/views/Settings.vue"
                            );
                        }

                    },
                    meta: {
                        middleware: [auth(false), parentOnly],
                        title: "pp.settings.title"
                    },
                    children: [
                        {
                            path: "",
                            name: "settings",
                            components: {
                                settingsSubview: () => {
                                    return import(
                                         "@/projects/parent_app/views/EditDetails.vue"
                                    );
                                }
                            },
                            meta: {
                                middleware: [auth(false), parentOnly],
                                title: "pp.settings.title"
                            }
                        },

                        {
                            path: "changepassword",
                            name: "change-password",
                            components: {
                                settingsSubview: () => {
                                    return import(
                                         "@/projects/parent_app/views/ChangePassword.vue"
                                    );
                                }
                            },
                            meta: {
                                middleware: [auth(false), parentOnly],
                                title: "pp.changepassword.title"
                            }
                        },
                        {
                            path: "deactivate",
                            name: "deactivate-account",
                            components: {
                                settingsSubview: () => {
                                    return import(
                                         "@/projects/parent_app/views/Deactivate.vue"
                                    );
                                }
                            },
                            meta: {
                                middleware: [auth(false), parentOnly],
                                title: "pp.deactivate.title"
                            }
                        },
                        {
                            path: "notifications",
                            name: "notifications",
                            components: {
                                settingsSubview: () => {
                                    return import(
                                         "@/projects/parent_app/views/Notifications.vue"
                                    );
                                }
                            },
                            meta: {
                                middleware: [auth(false), parentOnly],
                                title: "pp.deactivate.title"
                            }
                        }
                    ]
                },
                {
                    path: "select",
                    name: "child-select",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/parent_app/views/SelectChild.vue"
                            );
                        }
                    },
                    meta: {
                        middleware: [auth(false), parentOnly],
                        title: "pp.selectchild.title"
                    }
                }
            ]
        },
        {
            path: "/journal",
            component: () => {
                return import(
                     "@/projects/journal/App.vue"
                );
            },
            meta: {
                middleware: [auth(false), parentOnly],
                title: "journal"
            },
            children: [
                {
                    path: "users/:userId",
                    props: true,
                    name: "parent.journal.user",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/journal/views/UserEntries.vue"
                            );
                        },
                        sidebar1: () => {
                            return import(
                                 "@/projects/journal/sidebars/User.vue"
                            );
                        },
                        sidebar2: () => {
                            return import(
                                 "@/projects/journal/sidebars/UserFilter.vue"
                            );
                        }
                    },
                    meta: {
                        title: "parent.journal.user",
                        middleware: [userExists],
                    }
                },
                {
                    path: "users/:userId/entries/new",
                    name: "parent.journal.user.newentry",
                    props: true,
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/journal/views/CreateEntry.vue"
                            );
                        }
                    },
                    meta: {
                        title: "journal.class.newentry",
                        middleware: [userExists],
                    }
                },
                {
                    path: "users/:userId/journals/:classId/entries/new",
                    props: true,
                    name: "parent.journal.class.newentry",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/journal/views/CreateEntry.vue"
                            );
                        }
                    },
                    meta: {
                        title: "journal.class.newentry",
                        middleware: [userExists, journalClassExists],
                    }
                },
                {
                    path: "entries/:entryId",
                    name: "parent.journal.entry",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/journal/views/SingleEntry.vue"
                            );
                        }
                    },
                    meta: {
                        title: "parent.journal.entry",
                        middleware: [entryExists]
                    }
                },
                {
                    path: "entries/:entryId/edit",
                    name: "parent.journal.editentry",
                    props: true,
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/journal/views/CreateEntry.vue"
                            );
                        },
                    },
                    meta: {
                        title: "journal.class.editentry",
                        middleware: [entryExists],
                    }
                },
                {
                    path: "comments/:commentId",
                    name: "parent.journal.comment",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/journal/views/SingleComment.vue"
                            );
                        }
                    },
                    meta: {
                        title: "parent.journal.comment",
                        middleware: [commentExists]
                    }
                },
            ]
        }
    ];
} else {
    appRoutes = [
        ...resourceCenterRoutes,
        ...homework,
        ...courseRoutes,
        ...authRoutes,
        ...micRoutes,
        ...dashboardRoutes,
        ...journalRoutes,
        ...teacherAreaRoutes,
        {
            path: "/launch",
            name: "launch",
            component: () => {
                return import(
                     "@/projects/launch/App.vue"
                );
            },
            beforeEnter: () => {
                location.href = "/";
            },
            meta: {
                middleware: [auth()]
            }
        },
        {
            path: "/myfiles",
            name: "myfiles",
            component: () => {
                return import(
                     "@/projects/launch/App.vue"
                );
            },
            beforeEnter: () => {
                location.href = "/myfiles";
            },
            meta: {
                middleware: [auth()]
            }
        },
        {
            path: "/settings",
            component: () => {
                return import(
                     "@/projects/user_settings/App.vue"
                );
            },
            children: [
                {
                    path: "",
                    name: "settings",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/user_settings/views/Settings.vue"
                            );
                        }
                    },
                    meta: {
                        title: "settings.settings.title"
                    }
                },
                {
                    path: "changepassword",
                    name: "settings.changepassword",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/user_settings/views/ChangePassword.vue"
                            );
                        }
                    },
                    meta: {
                        title: "settings.changepassword.title"
                    }
                },
                {
                    path: "library",
                    name: "settings.library",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/user_settings/views/LibrarySettings.vue"
                            );
                        }
                    },
                    meta: {
                        title: "settings.library.title"
                    }
                },
                {
                    path: "scratch",
                    name: "settings.scratch",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/user_settings/views/ScratchSettings.vue"
                            );
                        }
                    },
                    meta: {
                        title: "settings.scratch.title"
                    }
                },
                {
                    path: "identities",
                    name: "settings.identities",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/user_settings/views/Identities.vue"
                            );
                        }
                    },
                    meta: {
                        title: "settings.identities.title"
                    }
                },
                {
                    path: "parents",
                    name: "settings.parents",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/user_settings/views/Parents.vue"
                            );
                        }
                    },
                    meta: {
                        title: "settings.parents.title"
                    }
                },
                {
                    path: "notifications",
                    name: "settings.notifications",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/user_settings/views/Notifications.vue"
                            );
                        }
                    },
                    meta: {
                        title: "settings.notifications.title",
                        middleware: [isStaff]
                    }
                }
            ],
            meta: {
                middleware: [auth()],
                authIncludes: ["user.rcuser"]
            }
        },
        {
            path: "/cl",
            name: "classlogin",
            component: () => {
                return import(
                     "@/projects/class_login/App.vue"
                );
            },
            children: [
                {
                    path: ":code",
                    name: "Class Login",
                    props: true,
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/class_login/views/ShowUserList.vue"
                            );
                        }
                    },
                    meta: {
                        title: "class_login",
                        middleware: [guest]
                    }
                }
            ]
        },
        {
            path: "/tests",
            component: () => {
                return import(
                     "@/projects/tests/App.vue"
                );
            },
            children: [
                {
                    path: "results",
                    name: "Test Results",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/tests/views/Report.vue"
                            );
                        }
                    }
                },
                {
                    path: ":testId",
                    name: "Test",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/tests/views/Test.vue"
                            );
                        }
                    }
                }
            ],
            meta: {
                middleware: [auth()]
            }
        },
        {
            path: "/dictionary",
            component: () => {
                return import(
                     "@/projects/dictionary/App.vue"
                );
            },
            children: [
                {
                    path: "",
                    name: "dictionary",
                    components: {
                        subview: () => {
                            return import(
                                 "@/projects/dictionary/components/dictionary.vue"
                            );
                        }
                    }
                }
            ],
            meta: {
                middleware: [auth(), isCompass]
            }
        }
    ];
}

const routes = [
    ...appRoutes,
    {
        path: "/install",
        name: "install",
        component: () =>
            import( "@/projects/app/App.vue")
        /*meta: {
            title: "install"
        }*/
    },
    {
        path: "/:catchAll(.*)*",
        name: "Not Found",
        component: () =>
            import(
                "@/projects/404Page/views/NotFound.vue"
            ),
        meta: {
            title: "notfound"
        }
    }
];

const router = createRouter({
    history: createWebHistory(),
    routes
});

// navigation guards
router.beforeEach(async (to, from) => {
    const authUserStore = useAuthUserStore();
    const formErrorsStore = useFormErrorsStore();

    formErrorsStore.clearErrors();
    let authIncludes = [];
    to.matched.forEach(x => {
        if (!x.meta.authIncludes) {
            return;
        }
        x.meta.authIncludes.forEach(y => {
            authIncludes.push(y);
        });
    });
    if (!authUserStore.check) {
        await authUserStore.getUser({
            authIncludes
        });
    }

    let language = "en-gb";
    if (authUserStore.check) {
        if (authUserStore.user?.language) {
            language = authUserStore.user.language;
        }
        if (
            authUserStore.user?.settings &&
            Object.prototype.hasOwnProperty.call(
                authUserStore.user?.settings,
                "language"
            )
        ) {
            language = authUserStore.user.settings.language;
        }
    }
    await loadLanguageAsync(language || "en-gb");

    let middleware = [];
    to.matched.forEach(x => {
        if (!x.meta.middleware) {
            return;
        }
        x.meta.middleware.forEach(y => {
            middleware.push(y);
        });
    });
    if (middleware.length === 0) {
        return;
    }

    for (let i = 0; i < middleware.length; i++) {
        let result = await middleware[i]({
            to,
            from
        });
        if (
            typeof result === "string" ||
            (typeof result === "object" &&
                (typeof result.path === "string" || typeof result.name === "string"))){
            return result;
        }
    }
});

export default router;
